import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import { Subscription } from "rxjs";
import {
  CanvasRenderDecision,
  ClippingStudioCanvasService,
  VideoFrameSource,
  WebGlViewportRect,
} from "app/js/src/infrastructure/clipping-studio/clipping-studio-canvas.service";
import { ClippingStudioGfxManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-gfx-manager.service";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";
import { computeInsets, SqzInsets } from "app/js/src/infrastructure/clipping-studio/clipping-studio-sqz-insets";
import {
  ClippingStudioSubtitleEvent,
  ClippingStudioTextEvent,
  ClippingStudioGraphicEvent,
  ReframeConfig,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { AppendedMamSegment } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { ClippingStudioClip } from "app/js/src/domain/clipping-studio/clipping-studio-clip";
import {
  ClippingStudioSubtitleStyle,
  SUBTITLE_BOX_PADDING,
  SUBTITLE_LINE_HEIGHT_RATIO,
  SUBTITLE_SHADOW_BLUR,
  SUBTITLE_SHADOW_COLOR,
  SUBTITLE_SHADOW_OFFSET_X,
  SUBTITLE_SHADOW_OFFSET_Y,
} from "app/js/src/domain/clipping-studio/clipping-studio-transcript";
import { resolveMamFileTimeMs } from "app/js/src/infrastructure/clipping-studio/clipping-studio-mam-file-time";
import { drawReframeFrame } from "app/js/src/infrastructure/clipping-studio/clipping-studio-reframe-draw";
import {
  CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY,
  CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT,
  dimensionsForAspectMaxHeight,
} from "app/js/src/domain/clipping-studio/clip-preview-image-encode";
import type { ClipPreviewAspectRatio } from "app/js/src/domain/clipping-studio/clip-preview-model";
import { ClippingStudioFontService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-font.service";
import {
  computeTextLayout,
  SIZE_REFERENCE_DIMENSION,
  TextLayout,
} from "app/js/src/domain/clipping-studio/clipping-studio-text-layout";

const DEFAULT_SUBTITLE_STYLE: ClippingStudioSubtitleStyle = {
  fontFamily: "sans-serif",
  fontSize: 24,
  fontColor: "rgba(255,255,255,1)",
  backgroundColor: "rgba(0,0,0,0.8)",
  backgroundStyle: "solid", // black box default
  position: "bottom",
};


const LOGO_URL_MAP: Record<string, string> = {
  RFI: "/assets/FTV/RFI.png",
  france24: "/assets/FTV/france24.png",
};

function getFruitIconUrl(iconKey: string | undefined): string {
  if (!iconKey) return "";
  return `/assets/FTV/icons/${iconKey}.svg`;
}

@Component({
  selector: "evrideo-clipping-studio-video-preview",
  templateUrl: "./clipping-studio-video-preview.component.html",
  styleUrls: ["./clipping-studio-video-preview.component.scss"],
})
export class ClippingStudioVideoPreviewComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() previewUrl: string | null = null;
  @Input() currentTimeMs = 0;
  @Input() isPlaying = false;
  @Input() captionsOn = false;
  @Input() muted = false;
  /** Playback speed (1 = normal). */
  @Input() playbackRate = 1;
  /** FPS for canvas render interval (e.g. state.media.fps). Fallback 30. */
  @Input() fps = 30;
  @Input() subtitles: ClippingStudioSubtitleEvent[] | null = null;
  @Input() subtitleStyle: ClippingStudioSubtitleStyle | null = null;
  @Input() text: ClippingStudioTextEvent[] | null = null;
  @Input() selectedTextId: string | null = null;
  @Input() graphics: ClippingStudioGraphicEvent[] | null = null;
  @Input() selectedGraphicId: string | null = null;
  @Input() isEditMode = false;
  /** Aspect ratio for the preview container (e.g. "16:9", "9:16"). Default "16:9". */
  @Input() aspectRatio: AspectRatio = AspectRatio.Aspect16x9;
  /** Optional reframe config for the current segment (edit mode + non-original destination). When set, video is drawn via 2D canvas with crop/letterbox/split. */
  @Input() reframeConfig: ReframeConfig | null = null;
  /** Main source duration (ms). When set with appendedMamSegments, getCurrentFrame switches to the correct video by currentTimeMs. */
  @Input() mainDurationMs = 0;
  /** Appended MAM segments on the source timeline. Each has previewUrl for playback. */
  @Input() appendedMamSegments: AppendedMamSegment[] = [];
  /** Clips in Edit mode; clips with previewUrl are used as video sources for playback. */
  @Input() editModeClips: ClippingStudioClip[] = [];
  /**
   * First linked audio preview URL for the main source when video/audio are separate files.
   * When set, main video is muted and this audio is synced to the playhead.
   */
  @Input() mainDefaultAudioPreviewUrl: string | null = null;
  /** When set (e.g. EDIT mode with clips), used only for overlay filtering (graphics, text). Clips and subtitles keep using currentTimeMs. */
  @Input() overlayTimeMs: number | null = null;
  @Input() gfxManager: ClippingStudioGfxManagerService | null = null;

  @Output() textSelected = new EventEmitter<string>();
  @Output() textPositionChange = new EventEmitter<{ id: string; x: number; y: number; isDragging?: boolean }>();
  @Output() textDragStart = new EventEmitter<void>();
  @Output() textDragEnd = new EventEmitter<void>();
  @Output() graphicSelected = new EventEmitter<string>();
  @Output() graphicPositionChange = new EventEmitter<{ id: string; x: number; y: number; isDragging?: boolean }>();
  @Output() graphicDragStart = new EventEmitter<void>();
  @Output() graphicDragEnd = new EventEmitter<void>();

  /** Time (ms) used only for filtering overlay graphics and text by inMs/outMs. When null, use currentTimeMs. */
  private get overlayFilterTimeMs(): number {
    if (this.overlayTimeMsOverride !== undefined) {
      return this.overlayTimeMsOverride ?? this.currentTimeMs;
    }
    return this.overlayTimeMs ?? this.currentTimeMs;
  }

  /** Temporary override for WYSIWYG export (does not mutate @Input overlayTimeMs). */
  private overlayTimeMsOverride: number | null | undefined = undefined;

  /** Bounds of currently drawn text items (canvas pixels), for hit-test. Updated in drawTextOverlays. */
  activeTextBounds: { id: string; left: number; top: number; right: number; bottom: number }[] = [];
  /** Bounds of currently drawn logos (center, half-size, rotation) for hit-test. Updated in drawMainOverlayFromRenderPayload. */
  private activeLogoBounds: { id: string; px: number; py: number; halfW: number; halfH: number; rad: number }[] = [];
  /**
   * Bounds of currently drawn non-SQZ GFX snapshots (canvas pixels), for hit-test / drag.
   * Stored in overlay pre-transform space so pointer coords mapped via mapOverlayPointerToPreSqzIfNeeded align.
   */
  private activeGfxBounds: { id: string; left: number; top: number; right: number; bottom: number }[] = [];

  /** When false, overlay draw skips selection chrome (used by WYSIWYG snapshot export). */
  private exportIncludeEditChrome = true;

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private hitTextId: string | null = null;
  private hitGraphicId: string | null = null;
  private isDragging = false;
  private isDraggingGraphic = false;
  private dragInitialNormX = 0;
  private dragInitialNormY = 0;
  private dragInitialCanvasX = 0;
  private dragInitialCanvasY = 0;
  private pointerDownCanvasX = 0;
  private pointerDownCanvasY = 0;
  private capturedPointerId: number | null = null;
  private static readonly LONG_PRESS_MS = 350;
  private static readonly MOVE_THRESHOLD_PX = 5;
  private logoImageCache = new Map<string, HTMLImageElement>();

  /** Incremental inset state for SQZ preview. Reset when the active SQZ timeline segment changes. */
  private lastSqzInsets: SqzInsets | undefined;
  private lastSqzTimelineGfxId: string | undefined;

  private lastRenderPayload: unknown;

  /** Last active source key (main / appended-N / editMam-id). When it changes, applyPlaybackToActiveSource runs. */
  private lastActiveSourceKey: string = "";
  /** Last clip id in edit order (Edit mode). When it changes, we seek so playback matches the new clip. */
  private lastActiveEditClipId: string | null = null;

  /**
   * Separate audio elements keyed by activeSourceKey (main / appended-N / editMam-id).
   * Built in TS (not template) so ViewChildren video refs stay aligned and clip reorder is safe.
   */
  private audioBySourceKey = new Map<string, HTMLAudioElement>();
  private static readonly AUDIO_DRIFT_SEC = 0.15;

  @ViewChild("video") videoRef: ElementRef<HTMLVideoElement>;
  @ViewChild("videoThumbnailCapture") videoThumbnailCaptureRef: ElementRef<HTMLVideoElement> | null = null;
  @ViewChildren("appendedVideo") appendedVideoRefs: QueryList<ElementRef<HTMLVideoElement>>;
  @ViewChildren("editMamVideo") editMamVideoRefs: QueryList<ElementRef<HTMLVideoElement>>;
  @ViewChild("previewCanvas") webGlPreviewCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild("previewCanvas2d") reframe2dPreviewCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild("mainOverlayCanvas") mainOverlayCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild("subtitlesCanvas") subtitlesCanvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild("gfxIframeContainer") gfxIframeContainerRef: ElementRef<HTMLDivElement>;
  @ViewChild("videoContainer") containerRef: ElementRef<HTMLDivElement>;

  private videoReady = false;
  private listenersAttached = false;
  private resizeObserver: ResizeObserver | null = null;

  /** Default timeout (ms) for whenVideoReady if not specified. */
  static readonly DEFAULT_LOAD_TIMEOUT_MS = 30_000;

  /** Pending whenVideoReady callbacks by media id (segment id or clip id). */
  private whenVideoReadyPending = new Map<
    string,
    { resolve: () => void; reject: (err: Error) => void; timeoutId?: ReturnType<typeof setTimeout> }
  >();
  private appendedVideoRefsSub?: Subscription;
  private editMamVideoRefsSub?: Subscription;

  /** True once the video has loaded data and the first frame is available (used to show "Loading video..." until then). */
  videoFrameReady = false;

  private fontEnsureToken = 0;

  constructor(
    private canvasService: ClippingStudioCanvasService,
    private cdr: ChangeDetectorRef,
    private fontService: ClippingStudioFontService,
  ) {}

  ngAfterViewInit(): void {
    this.rebuildSeparateAudioElements();
    this.trySetupVideoAndContainer();
    this.subscribeToNonMainVideoRefsChangesForWhenReady();
    if (this.gfxManager && this.gfxIframeContainerRef?.nativeElement) {
      this.gfxManager.setContainer(this.gfxIframeContainerRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.appendedVideoRefsSub?.unsubscribe();
    this.appendedVideoRefsSub = undefined;
    this.editMamVideoRefsSub?.unsubscribe();
    this.editMamVideoRefsSub = undefined;
    this.disposeAllSeparateAudio();
    this.rejectAllWhenVideoReadyPending(new Error("Component destroyed"));
    this.canvasService.stopRender();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.syncGfxSignals(changes);

    if (changes["previewUrl"] && !this.previewUrl) {
      this.canvasService.stopRender();
      this.listenersAttached = false;
      this.videoFrameReady = false;
    }

    if (changes["previewUrl"] && this.previewUrl) {
      this.videoFrameReady = false;
      this.cdr.markForCheck();
      setTimeout(() => this.trySetupVideoAndContainer(), 0);
    }

    if (changes["gfxManager"] && this.gfxManager && this.gfxIframeContainerRef?.nativeElement) {
      this.gfxManager.setContainer(this.gfxIframeContainerRef.nativeElement);
    }

    const audioSourcesChanged =
      !!changes["mainDefaultAudioPreviewUrl"] ||
      !!changes["appendedMamSegments"] ||
      !!changes["editModeClips"];
    if (audioSourcesChanged) {
      this.rebuildSeparateAudioElements();
    }

    if (changes["text"] || changes["subtitleStyle"]) {
      this.ensurePreviewFontsLoaded();
    }

    if (!this.videoRef?.nativeElement || !this.videoReady) {
      if (audioSourcesChanged) {
        setTimeout(() => this.applyPlaybackToActiveSource(), 0);
      }
      return;
    }

    const video = this.videoRef.nativeElement;
    const hasMultiSource =
      !!this.appendedMamSegments?.length || this.orderedEditMamClips.length > 0;
    const hasSeparateAudio = this.audioBySourceKey.size > 0;

    if (changes["isPlaying"]) {
      if (hasMultiSource || hasSeparateAudio) {
        this.lastActiveSourceKey = "";
        this.lastActiveEditClipId = null;
        setTimeout(() => this.applyPlaybackToActiveSource(), 0);
      } else {
        if (this.isPlaying) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    }

    if (changes["muted"] && hasSeparateAudio) {
      const activeAudio = this.getActiveSeparateAudio();
      if (activeAudio) {
        activeAudio.muted = this.muted;
      }
    }

    if (changes["currentTimeMs"]) {
      const editClipBoundaryCrossed =
        this.isEditMode &&
        (this.editModeClips?.length ?? 0) > 0 &&
        this.currentEditClipId !== this.lastActiveEditClipId;
      if (hasMultiSource || editClipBoundaryCrossed) {
        const key = this.activeSourceKey;
        if (key !== this.lastActiveSourceKey || editClipBoundaryCrossed) {
          setTimeout(() => this.applyPlaybackToActiveSource(), 0);
        } else if (hasSeparateAudio) {
          this.syncSeparateAudio(false);
        }
      } else if (hasSeparateAudio) {
        this.syncSeparateAudio(false);
      }
    }

    if (changes["appendedMamSegments"] && this.appendedMamSegments?.length) {
      this.lastActiveSourceKey = "";
      this.lastActiveEditClipId = null;
      setTimeout(() => this.applyPlaybackToActiveSource(), 0);
    }

    if (changes["editModeClips"] && this.orderedEditMamClips.length > 0) {
      this.lastActiveSourceKey = "";
      this.lastActiveEditClipId = null;
      setTimeout(() => this.applyPlaybackToActiveSource(), 0);
    }

    if (changes["mainDefaultAudioPreviewUrl"] && hasSeparateAudio) {
      this.lastActiveSourceKey = "";
      setTimeout(() => this.applyPlaybackToActiveSource(), 0);
    }

    if (changes["playbackRate"]) {
      video.playbackRate = this.playbackRate;
      const activeVideo = this.getActiveVideoElement();
      if (activeVideo) activeVideo.playbackRate = this.playbackRate;
      const activeAudio = this.getActiveSeparateAudio();
      if (activeAudio) activeAudio.playbackRate = this.playbackRate;
    }

    if (changes["aspectRatio"]) {
      this.gfxManager?.setCurrentAspectRatio(this.aspectRatio);
      this.resizeVideoAndCanvas();
    }

    if (changes["reframeConfig"]) {
      const hadReframe = changes["reframeConfig"].previousValue != null;
      const hasReframe = this.reframeConfig != null;
      if (hadReframe && !hasReframe) {
        this.canvasService.stopRender();
        setTimeout(() => this.initCanvasAndResize(), 0);
      } else if (!hadReframe && hasReframe) {
        this.canvasService.stopRender();
        setTimeout(() => this.initCanvasAndResize(), 0);
      } else if (hasReframe) {
        this.canvasService.setReframeConfig(this.reframeConfig);
        setTimeout(() => this.resizeVideoAndCanvas(), 0);
      }
    }

    if (
      changes["currentTimeMs"] ||
      changes["captionsOn"] ||
      changes["subtitles"] ||
      changes["subtitleStyle"] ||
      changes["reframeConfig"]
    ) {
      this.drawSubtitles();
    }
  }

  /**
   * Run after view has been updated so that *ngIf="previewUrl" has created video/canvas.
   * Attaches video listeners and ResizeObserver once; inits canvas when video has data.
   */
  private trySetupVideoAndContainer(): void {
    const video = this.videoRef?.nativeElement;
    const container = this.containerRef?.nativeElement;

    if (video && !this.listenersAttached) {
      this.listenersAttached = true;
      video.addEventListener("loadedmetadata", () => {
        this.videoReady = true;
        video.playbackRate = this.playbackRate;
        this.syncTime();
      });
      video.addEventListener("loadeddata", () => {
        this.videoFrameReady = true;
        this.cdr.markForCheck();
        this.initCanvasAndResize();
      });
    }

    if (container && !this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() =>
        this.resizeVideoAndCanvas(),
      );
      this.resizeObserver.observe(container);
    }

    if (video && video.readyState >= 2) {
      this.videoFrameReady = true;
      this.cdr.markForCheck();
      this.initCanvasAndResize();
    }
  }

  /**
   * Ensures the correct preview canvas exists, wires `ClippingStudioCanvasService`
   * (frame provider, `decideAndPrepareFrame` / `drawMainOverlayFromRenderPayload`),
   * resizes surfaces, then starts the service render loop once per mode.
   * Aligns `ClippingStudioCanvasService` active base-layer backend with `reframeConfig`
   * before `init` (single entry for `loadeddata`, `reframeConfig` changes, etc.).
   */
  private initCanvasAndResize(): void {
    if (!this.videoRef?.nativeElement) return;

    const reframe = this.reframeConfig != null;
    if (reframe) {
      this.canvasService.setActiveBaseLayerToCanvas2d();
    } else {
      this.canvasService.setActiveBaseLayerToWebGl();
    }
    this.canvasService.setReframeConfig(this.reframeConfig);

    const targetRef = reframe ? this.reframe2dPreviewCanvasRef : this.webGlPreviewCanvasRef;
    if (!this.wireCanvasServiceToCanvas(targetRef)) return;

    this.resizeVideoAndCanvas();

    this.canvasService.startRender();
  }

  /**
   * Configures `ClippingStudioCanvasService` for the given base canvas: `init` with
   * FPS interval, frame provider, `beforeDraw` → `decideAndPrepareFrame`, `afterRender` →
   * `drawMainOverlayFromRenderPayload`. Returns false if the ref has no native element.
   */
  private wireCanvasServiceToCanvas(
    targetCanvasRef: ElementRef<HTMLCanvasElement> | undefined,
  ): boolean {
    if (!targetCanvasRef?.nativeElement) return false;

    const intervalMs = this.fps > 0 ? 1000 / this.fps : 1000 / 30;
    this.canvasService.init(targetCanvasRef, intervalMs);
    this.canvasService.setFrameProvider(() => this.getCurrentFrame());
    this.canvasService.setBeforeDrawCallback((cw, ch) =>
      this.decideAndPrepareFrame(cw, ch),
    );
    this.canvasService.setAfterRenderCallback((payload) =>
      this.drawMainOverlayFromRenderPayload(payload),
    );
    return true;
  }

  /** Index of the video source that should play audio and respond to play/pause: -1 = main, 0..n-1 = appended segment index. */
  get activeSourceIndex(): number {
    const appended = this.appendedMamSegments ?? [];
    if (appended.length === 0 || this.currentTimeMs < this.mainDurationMs) return -1;
    let cursorMs = this.mainDurationMs;
    for (let i = 0; i < appended.length; i++) {
      const endMs = cursorMs + appended[i].durationMs;
      if (this.currentTimeMs < endMs) return i;
      cursorMs = endMs;
    }
    return -1;
  }

  /** Edit mode: clips that have previewUrl (MAM-sourced), sorted by order. */
  get orderedEditMamClips(): ClippingStudioClip[] {
    const list = this.editModeClips ?? [];
    const withPreview = list.filter((c) => c.previewUrl != null && c.previewUrl !== "");
    return withPreview.slice().sort((a, b) => a.order - b.order);
  }

  /** Edit mode: all clips sorted by order. Used to detect clip-boundary crossing. */
  private get orderedEditModeClips(): ClippingStudioClip[] {
    const list = this.editModeClips ?? [];
    return list.slice().sort((a, b) => a.order - b.order);
  }

  /** Edit mode: id of the clip containing currentTimeMs, or null. Changes when playhead crosses to another clip. */
  private get currentEditClipId(): string | null {
    if (!this.isEditMode) return null;
    const ordered = this.orderedEditModeClips;
    const clip = ordered.find((c) => this.currentTimeMs >= c.inMs && this.currentTimeMs < c.outMs);
    return clip?.id ?? null;
  }

  /** Key that changes when playhead moves to a different source (main, appended segment, or edit MAM clip). Used to trigger applyPlaybackToActiveSource. */
  get activeSourceKey(): string {
    const editMam = this.orderedEditMamClips;
    if (this.isEditMode && editMam.length > 0) {
      const clip = editMam.find((c) => this.currentTimeMs >= c.inMs && this.currentTimeMs < c.outMs);
      if (clip) return `editMam-${clip.id}`;
    }
    const idx = this.activeSourceIndex;
    if (idx === -1) return "main";
    return `appended-${idx}`;
  }

  /** Time in seconds within the active source (for syncing video.currentTime for audio). Computed from data only, no getCurrentFrame(). */
  private getActiveTimeSec(): number {
    const key = this.activeSourceKey;
    if (!key || key === "main") {
      return Math.max(0, this.currentTimeMs / 1000);
    }
    if (key.startsWith("appended-")) {
      const idx = parseInt(key.replace("appended-", ""), 10);
      if (Number.isNaN(idx)) return 0;
      const appended = this.appendedMamSegments ?? [];
      let cursorMs = this.mainDurationMs;
      for (let i = 0; i < idx && i < appended.length; i++) {
        cursorMs += appended[i].durationMs;
      }
      const timeInSegmentMs = this.currentTimeMs - cursorMs;
      return Math.max(0, timeInSegmentMs / 1000);
    }
    if (key.startsWith("editMam-")) {
      const clipId = key.replace("editMam-", "");
      const editMam = this.orderedEditMamClips;
      const clip = editMam.find((c) => c.id === clipId);
      if (clip) {
        return (
          resolveMamFileTimeMs(
            this.currentTimeMs,
            clip,
            this.appendedMamSegments ?? [],
            this.mainDurationMs,
          ) / 1000
        );
      }
      return 0;
    }
    return 0;
  }

  /** Returns the video element that should play audio for the current activeSourceKey. Uses refs only, no getCurrentFrame(). */
  private getActiveVideoElement(): HTMLVideoElement | null {
    const mainVideo = this.videoRef?.nativeElement;
    const key = this.activeSourceKey;
    if (!key || key === "main") return mainVideo ?? null;
    if (key.startsWith("appended-")) {
      const idx = parseInt(key.replace("appended-", ""), 10);
      if (Number.isNaN(idx)) return null;
      const refs = this.appendedVideoRefs?.toArray() ?? [];
      return refs[idx]?.nativeElement ?? null;
    }
    if (key.startsWith("editMam-")) {
      const clipId = key.replace("editMam-", "");
      const editMam = this.orderedEditMamClips;
      const idx = editMam.findIndex((c) => c.id === clipId);
      if (idx < 0) return null;
      const refs = this.editMamVideoRefs?.toArray() ?? [];
      return refs[idx]?.nativeElement ?? null;
    }
    return null;
  }

  /** Pause all videos, set currentTime on the active one, and play it if isPlaying. Ensures only the active source has audio. Uses key + refs only, not getCurrentFrame(). */
  private applyPlaybackToActiveSource(): void {
    const mainVideo = this.videoRef?.nativeElement;
    const appendedRefs = this.appendedVideoRefs?.toArray() ?? [];
    const editMamRefs = this.editMamVideoRefs?.toArray() ?? [];
    const allVideos: HTMLVideoElement[] = [];
    if (mainVideo) allVideos.push(mainVideo);
    appendedRefs.forEach((r) => {
      if (r?.nativeElement) allVideos.push(r.nativeElement);
    });
    editMamRefs.forEach((r) => {
      if (r?.nativeElement) allVideos.push(r.nativeElement);
    });

    for (const v of allVideos) v.pause();

    const activeVideo = this.getActiveVideoElement();
    const timeSec = this.getActiveTimeSec();
    if (activeVideo) {
      if (Math.abs(activeVideo.currentTime - timeSec) > 0.05) {
        activeVideo.currentTime = timeSec;
      }
      activeVideo.playbackRate = this.playbackRate;
      if (this.isPlaying) activeVideo.play().catch(() => {});
    }
    this.syncSeparateAudio(true);
    this.lastActiveSourceKey = this.activeSourceKey;
    this.lastActiveEditClipId = this.currentEditClipId;
  }

  /** True when main source uses a separate linked audio file (video must stay muted). */
  get hasMainSeparateAudio(): boolean {
    return !!this.mainDefaultAudioPreviewUrl?.trim();
  }

  /** True when appended segment i uses a separate linked audio file. */
  hasAppendedSeparateAudio(index: number): boolean {
    const url = this.appendedMamSegments?.[index]?.defaultAudioPreviewUrl;
    return !!url?.trim();
  }

  /** True when edit-mode MAM clip uses a separate linked audio file. */
  hasEditMamSeparateAudio(clip: ClippingStudioClip): boolean {
    return !!clip?.defaultAudioPreviewUrl?.trim();
  }

  private getActiveSeparateAudio(): HTMLAudioElement | null {
    return this.audioBySourceKey.get(this.activeSourceKey) ?? null;
  }

  private disposeAllSeparateAudio(): void {
    for (const audio of this.audioBySourceKey.values()) {
      try {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      } catch {
        // ignore dispose errors
      }
    }
    this.audioBySourceKey.clear();
  }

  private rebuildSeparateAudioElements(): void {
    this.disposeAllSeparateAudio();
    const add = (key: string, url: string | undefined | null) => {
      if (!url?.trim()) return;
      const audio = new Audio();
      audio.preload = "auto";
      audio.crossOrigin = "anonymous";
      audio.src = url;
      this.audioBySourceKey.set(key, audio);
    };
    add("main", this.mainDefaultAudioPreviewUrl);
    (this.appendedMamSegments ?? []).forEach((seg, i) => {
      add(`appended-${i}`, seg.defaultAudioPreviewUrl);
    });
    this.orderedEditMamClips.forEach((clip) => {
      add(`editMam-${clip.id}`, clip.defaultAudioPreviewUrl);
    });
  }

  /**
   * Syncs separate audio elements to the playhead: pause inactive, seek/play active.
   * When forceSeek is false, only corrects drift above AUDIO_DRIFT_SEC.
   */
  private syncSeparateAudio(forceSeek: boolean): void {
    if (this.audioBySourceKey.size === 0) return;
    const activeKey = this.activeSourceKey;
    const timeSec = this.getActiveTimeSec();
    for (const [key, audio] of this.audioBySourceKey) {
      if (key !== activeKey) {
        if (!audio.paused) {
          audio.pause();
        }
        continue;
      }
      audio.muted = this.muted;
      audio.playbackRate = this.playbackRate;
      if (
        forceSeek ||
        Math.abs(audio.currentTime - timeSec) >
          ClippingStudioVideoPreviewComponent.AUDIO_DRIFT_SEC
      ) {
        try {
          audio.currentTime = timeSec;
        } catch {
          // ignore seek errors while metadata loads
        }
      }
      if (this.isPlaying) {
        if (audio.paused) {
          audio.play().catch(() => {});
        }
      } else if (!audio.paused) {
        audio.pause();
      }
    }
  }

  /**
   * Clears SQZ inset incremental cache; next frame recomputes from full snapshot.
   */
  clearSqzInsetCache(): void {
    this.lastSqzInsets = undefined;
  }

  /**
   * Returns a promise that resolves when the video element for the given segment or clip id has loaded enough to play through (readyState >= 4, canplaythrough).
   * Rejects after timeoutMs if provided. Used when adding MAM media so the UI can wait before allowing interaction.
   */
  whenVideoReady(mediaId: string, timeoutMs?: number): Promise<void> {
    const effectiveTimeout = timeoutMs ?? ClippingStudioVideoPreviewComponent.DEFAULT_LOAD_TIMEOUT_MS;
    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const entry = this.whenVideoReadyPending.get(mediaId);
        if (entry) {
          this.whenVideoReadyPending.delete(mediaId);
          entry.reject(new Error("Media load timeout"));
        }
      }, effectiveTimeout);

      this.whenVideoReadyPending.set(mediaId, { resolve, reject, timeoutId });
      setTimeout(() => this.tryResolvePendingVideoReady(mediaId), 0);
    });
  }

  private rejectAllWhenVideoReadyPending(err: Error): void {
    for (const [id, entry] of this.whenVideoReadyPending) {
      if (entry.timeoutId) clearTimeout(entry.timeoutId);
      entry.reject(err);
    }
    this.whenVideoReadyPending.clear();
  }

  private subscribeToNonMainVideoRefsChangesForWhenReady(): void {
    this.appendedVideoRefsSub?.unsubscribe();
    this.appendedVideoRefsSub = this.appendedVideoRefs?.changes?.subscribe(() => {
      this.tryResolveAllPendingVideoReady();
      this.lastActiveSourceKey = "";
      this.lastActiveEditClipId = null;
      setTimeout(() => this.applyPlaybackToActiveSource(), 0);
    });
    this.editMamVideoRefsSub?.unsubscribe();
    this.editMamVideoRefsSub = this.editMamVideoRefs?.changes?.subscribe(() => {
      this.tryResolveAllPendingVideoReady();
      this.lastActiveSourceKey = "";
      this.lastActiveEditClipId = null;
      setTimeout(() => this.applyPlaybackToActiveSource(), 0);
    });
    setTimeout(() => this.tryResolveAllPendingVideoReady(), 0);
  }

  private tryResolveAllPendingVideoReady(): void {
    for (const id of this.whenVideoReadyPending.keys()) {
      this.tryResolvePendingVideoReady(id);
    }
  }

  private tryResolvePendingVideoReady(mediaId: string): void {
    const entry = this.whenVideoReadyPending.get(mediaId);
    if (!entry) return;

    const appended = this.appendedMamSegments ?? [];
    const appendedIdx = appended.findIndex((s) => s.id === mediaId);
    if (appendedIdx >= 0) {
      const refs = this.appendedVideoRefs?.toArray() ?? [];
      const videoEl = refs[appendedIdx]?.nativeElement;
      if (videoEl) {
        if (videoEl.readyState >= 4) {
          if (entry.timeoutId) clearTimeout(entry.timeoutId);
          this.whenVideoReadyPending.delete(mediaId);
          entry.resolve();
          return;
        }
        videoEl.addEventListener(
          "canplaythrough",
          () => {
            const e = this.whenVideoReadyPending.get(mediaId);
            if (e) {
              if (e.timeoutId) clearTimeout(e.timeoutId);
              this.whenVideoReadyPending.delete(mediaId);
              e.resolve();
            }
          },
          { once: true },
        );
      }
      return;
    }

    const editMam = this.orderedEditMamClips;
    const editIdx = editMam.findIndex((c) => c.id === mediaId);
    if (editIdx >= 0) {
      const refs = this.editMamVideoRefs?.toArray() ?? [];
      const videoEl = refs[editIdx]?.nativeElement;
      if (videoEl) {
        if (videoEl.readyState >= 4) {
          if (entry.timeoutId) clearTimeout(entry.timeoutId);
          this.whenVideoReadyPending.delete(mediaId);
          entry.resolve();
          return;
        }
        videoEl.addEventListener(
          "canplaythrough",
          () => {
            const e = this.whenVideoReadyPending.get(mediaId);
            if (e) {
              if (e.timeoutId) clearTimeout(e.timeoutId);
              this.whenVideoReadyPending.delete(mediaId);
              e.resolve();
            }
          },
          { once: true },
        );
      }
    }
  }

  private getCurrentFrame(): VideoFrameSource | null {
    const mainVideo = this.videoRef?.nativeElement;
    if (!mainVideo || !this.previewUrl) return null;

    const editMam = this.orderedEditMamClips;
    if (this.isEditMode && editMam.length > 0) {
      const clip = editMam.find((c) => this.currentTimeMs >= c.inMs && this.currentTimeMs < c.outMs);
      if (clip?.previewUrl) {
        const refs = this.editMamVideoRefs?.toArray() ?? [];
        const idx = editMam.indexOf(clip);
        const videoEl = refs[idx]?.nativeElement;
        if (videoEl) {
          if (videoEl.readyState < 2) return null;
          const timeInClipSec =
            resolveMamFileTimeMs(
              this.currentTimeMs,
              clip,
              this.appendedMamSegments ?? [],
              this.mainDurationMs,
            ) / 1000;
          return {
            video: videoEl,
            timeSec: Math.max(0, timeInClipSec),
          };
        }
      }
    }

    const appended = this.appendedMamSegments ?? [];
    if (appended.length === 0 || this.currentTimeMs < this.mainDurationMs) {
      return {
        video: mainVideo,
        timeSec: Math.max(0, this.currentTimeMs / 1000),
      };
    }

    let cursorMs = this.mainDurationMs;
    const refs = this.appendedVideoRefs?.toArray() ?? [];
    for (let i = 0; i < appended.length; i++) {
      const seg = appended[i];
      const endMs = cursorMs + seg.durationMs;
      if (this.currentTimeMs < endMs) {
        const videoEl = refs[i]?.nativeElement;
        if (!videoEl) return null;
        if (videoEl.readyState < 2) return null;
        const timeInSegmentMs = this.currentTimeMs - cursorMs;
        return {
          video: videoEl,
          timeSec: Math.max(0, timeInSegmentMs / 1000),
        };
      }
      cursorMs = endMs;
    }

    return {
      video: mainVideo,
      timeSec: Math.max(0, this.mainDurationMs / 1000),
    };
  }

  /**
   * Resolves a timeline position (ms) to the video element and time-in-source (seconds).
   * Used for captureSnapshotAtTime and any caller that needs the correct source for a given time.
   */
  private resolveTimelineTimeToSource(
    timeMs: number,
  ): { video: HTMLVideoElement; timeSec: number } | null {
    const mainVideo = this.videoRef?.nativeElement;
    if (!mainVideo || !this.previewUrl) return null;

    const editMam = this.orderedEditMamClips;
    if (this.isEditMode && editMam.length > 0) {
      const clip = editMam.find((c) => timeMs >= c.inMs && timeMs < c.outMs);
      if (clip?.previewUrl) {
        const refs = this.editMamVideoRefs?.toArray() ?? [];
        const idx = editMam.indexOf(clip);
        const videoEl = refs[idx]?.nativeElement;
        if (videoEl) {
          const timeInClipSec =
            resolveMamFileTimeMs(
              timeMs,
              clip,
              this.appendedMamSegments ?? [],
              this.mainDurationMs,
            ) / 1000;
          return {
            video: videoEl,
            timeSec: Math.max(0, timeInClipSec),
          };
        }
      }
    }

    const appended = this.appendedMamSegments ?? [];
    if (appended.length === 0 || timeMs < this.mainDurationMs) {
      return {
        video: mainVideo,
        timeSec: Math.max(0, timeMs / 1000),
      };
    }

    let cursorMs = this.mainDurationMs;
    const refs = this.appendedVideoRefs?.toArray() ?? [];
    for (let i = 0; i < appended.length; i++) {
      const seg = appended[i];
      const endMs = cursorMs + seg.durationMs;
      if (timeMs < endMs) {
        const videoEl = refs[i]?.nativeElement;
        if (!videoEl || videoEl.readyState < 2) return null;
        const timeInSegmentMs = timeMs - cursorMs;
        return {
          video: videoEl,
          timeSec: Math.max(0, timeInSegmentMs / 1000),
        };
      }
      cursorMs = endMs;
    }

    return {
      video: mainVideo,
      timeSec: Math.max(0, this.mainDurationMs / 1000),
    };
  }

  private resizeVideoAndCanvas(): void {
    const container = this.containerRef?.nativeElement;
    const video = this.videoRef?.nativeElement;
    if (!container || !video) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth <= 0 || containerHeight <= 0) return;

    const [num = 16, den = 9] = this.aspectRatio.split(":").map((s) => parseFloat(s.trim()) || 0);
    const ratio = num > 0 && den > 0 ? num / den : 16 / 9;

    let width = containerWidth;
    let height = width / ratio;
    if (height > containerHeight) {
      height = containerHeight;
      width = height * ratio;
    }

    const w = Math.floor(width);
    const h = Math.floor(height);
    this.resizeVideoElement(video, w, h);

    this.resizeBaseCanvas(w, h);

    const mainOverlay = this.mainOverlayCanvasRef?.nativeElement;
    if (mainOverlay) {
      this.resizeOverlayCanvas(mainOverlay, w, h, containerWidth, containerHeight);
    }

    const subtitles = this.subtitlesCanvasRef?.nativeElement;
    if (subtitles) {
      this.resizeOverlayCanvas(subtitles, w, h, containerWidth, containerHeight);
    }

    this.drawSubtitles();
  }

  private resizeVideoElement(video: HTMLVideoElement, w: number, h: number): void {
    video.width = w;
    video.height = h;
    video.style.width = `${w}px`;
    video.style.height = `${h}px`;
  }

  private resizeBaseCanvas(w: number, h: number): void {
    if (this.reframeConfig != null) {
      const canvas2d = this.reframe2dPreviewCanvasRef?.nativeElement;
      if (canvas2d) {
        this.canvasService.resize(w, h);
        canvas2d.style.width = `${w}px`;
        canvas2d.style.height = `${h}px`;
      }
    } else {
      const canvas = this.webGlPreviewCanvasRef?.nativeElement;
      if (canvas) {
        this.canvasService.resize(w, h);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }
    }
  }

  private resizeOverlayCanvas(
    canvas: HTMLCanvasElement,
    w: number,
    h: number,
    containerWidth: number,
    containerHeight: number,
  ): void {
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.style.left = `${(containerWidth - w) / 2}px`;
    canvas.style.top = `${(containerHeight - h) / 2}px`;
  }

  private syncTime(): void {
    if (!this.videoRef?.nativeElement || !this.videoReady) return;
    const video = this.videoRef.nativeElement;
    if (video.seeking) return;
    const targetSec = this.currentTimeMs / 1000;
    if (Math.abs(video.currentTime - targetSec) > 0.1) {
      video.currentTime = targetSec;
    }
  }

  /**
   * Forward reactive inputs into gfxManager signals (`editTimeMs`, `effectiveGraphics`).
   * Seek aligns with snapshots: `seekVisibleForPreview` runs on the same render path as overlay draw.
   */
  private syncGfxSignals(changes: SimpleChanges): void {
    if (!this.gfxManager) return;

    if (changes["currentTimeMs"] || changes["overlayTimeMs"] || changes["gfxManager"]) {
      this.gfxManager.setEditTimeMs(this.overlayFilterTimeMs);
    }
    if (changes["graphics"] || changes["gfxManager"]) {
      this.gfxManager.setEffectiveGraphics(this.graphics ?? []);
    }
  }

  private decideAndPrepareFrame(
    canvasW: number,
    canvasH: number,
  ): CanvasRenderDecision {
    // GFX (incl. SQZ) is edit-timeline only — same contract as text/emoji/logo overlays.
    if (!this.isEditMode) {
      if (this.lastSqzTimelineGfxId != null) {
        this.lastSqzInsets = undefined;
        this.lastSqzTimelineGfxId = undefined;
      }
      const payload = { mode: "nonSqz", visible: [] as any[] };
      this.lastRenderPayload = payload;
      return { payload };
    }

    const manager = this.gfxManager;
    if (!manager) {
      const payload = { mode: "nonSqz", visible: [] as any[] };
      this.lastRenderPayload = payload;
      return { payload };
    }

    manager.setEditTimeMs(this.overlayFilterTimeMs);
    manager.seekVisibleForPreview();
    const visible = manager.visibleGfxEntries();

    const sqzEntry = visible.find((e) => e.graphic.isSqz === true);
    if (!sqzEntry) {
      if (this.lastSqzTimelineGfxId != null) {
        this.lastSqzInsets = undefined;
        this.lastSqzTimelineGfxId = undefined;
      }
      const payload = { mode: "nonSqz", visible };
      this.lastRenderPayload = payload;
      return { payload };
    }

    if (sqzEntry.timelineGfxId !== this.lastSqzTimelineGfxId) {
      this.lastSqzInsets = undefined;
      this.lastSqzTimelineGfxId = sqzEntry.timelineGfxId;
    }

    const sqzSnapshot = manager.captureSnapshot(sqzEntry.timelineGfxId);
    if (!sqzSnapshot) {
      const payload = { mode: "nonSqz", visible };
      this.lastRenderPayload = payload;
      return { payload };
    }
    const sqzCtx = sqzSnapshot.getContext("2d");
    if (!sqzCtx) {
      const payload = { mode: "nonSqz", visible };
      this.lastRenderPayload = payload;
      return { payload };
    }

    const imageData = sqzCtx.getImageData(0, 0, sqzSnapshot.width, sqzSnapshot.height);
    const insets = computeInsets(imageData, this.lastSqzInsets);
    this.lastSqzInsets = insets;

    const stageW = sqzSnapshot.width;
    const stageH = sqzSnapshot.height;
    const scaleX = stageW > 0 ? (stageW - insets.left - insets.right) / stageW : 1;
    const scaleY = stageH > 0 ? (stageH - insets.top - insets.bottom) / stageH : 1;

    const previewScaleX = canvasW / stageW;
    const previewScaleY = canvasH / stageH;

    const leftPx = insets.left * previewScaleX;
    const topPx = insets.top * previewScaleY;
    const bottomPx = insets.bottom * previewScaleY;
    const innerW = (stageW - insets.left - insets.right) * previewScaleX;
    const innerH = (stageH - insets.top - insets.bottom) * previewScaleY;

    const viewport: WebGlViewportRect | undefined =
      innerW > 0 && innerH > 0
        ? { x: leftPx, y: bottomPx, w: innerW, h: innerH }
        : undefined;

    const dstRect2d =
      innerW > 0 && innerH > 0
        ? { x: leftPx, y: topPx, w: innerW, h: innerH }
        : undefined;

    const payload = {
      mode: "sqz",
      visible,
      sqzSnapshot,
      stageW,
      stageH,
      scaleX,
      scaleY,
      leftPx,
      topPx,
    };
    this.lastRenderPayload = payload;

    return { viewport, dstRect2d, payload };
  }

  private drawMainOverlayFromRenderPayload(payload: unknown): void {

    const canvas = this.mainOverlayCanvasRef?.nativeElement;
    if (!canvas?.width || !canvas?.height) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    const p: any = payload ?? this.lastRenderPayload;

    ctx.clearRect(0, 0, w, h);

    // Defense-in-depth: never composite GFX/SQZ outside EDIT (incl. stale payloads).
    if (!this.isEditMode) {
      this.drawNonSqzOverlay(ctx, w, h, []);
      return;
    }

    if (!p || p.mode !== "sqz") {
      // Non-SQZ: draw normal overlay (with hit-test bounds) + GFX snapshots.
      this.drawNonSqzOverlay(ctx, w, h, p?.visible ?? []);
      return;
    }

    const manager = this.gfxManager;
    if (!manager) return;

    // SQZ: draw squeezable overlay in transform, then SQZ art full-frame.
    ctx.save();
    ctx.translate(p.leftPx, p.topPx);
    ctx.scale(p.scaleX, p.scaleY);
    this.drawSqueezableOverlayContent(ctx, w, h);
    this.activeGfxBounds = [];
    for (const entry of p.visible ?? []) {
      if (entry.graphic?.isSqz === true) continue;
      const snapshot = manager.captureSnapshot(entry.timelineGfxId);
      if (!snapshot) continue;
      const g = entry.graphic;
      const base = manager.getCurrentBaseStageSize();
      const gfxDrawW = snapshot.width * (w / base.w);
      const gfxDrawH = snapshot.height * (h / base.h);
      const gfxX = (g.x ?? 0) * w;
      const gfxY = (g.y ?? 0) * h;
      ctx.drawImage(snapshot, gfxX, gfxY, gfxDrawW, gfxDrawH);
      if (this.isEditMode) {
        this.activeGfxBounds.push({
          id: g.id,
          left: gfxX,
          top: gfxY,
          right: gfxX + gfxDrawW,
          bottom: gfxY + gfxDrawH,
        });
        this.strokeNonSqzGfxEditBounds(ctx, gfxX, gfxY, gfxDrawW, gfxDrawH, g.id);
      }
    }
    ctx.restore();

    ctx.drawImage(p.sqzSnapshot, 0, 0, w, h);
  }

  private drawNonSqzOverlay(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    visible: { timelineGfxId: string; localMs: number; graphic: ClippingStudioGraphicEvent }[],
  ): void {
    // --- Text overlays ---
    if (this.isEditMode) {
      this.drawTextOverlays(ctx, w, h);
    } else {
      this.activeTextBounds = [];
    }

    // --- Logos, fruit-icons, emojis ---
    this.activeLogoBounds = [];
    if (this.isEditMode && this.graphics?.length) {
      const activeGraphics = this.graphics.filter(
        (g) =>
          (g.type === "logo" || g.type === "fruit-icon" || g.type === "emoji") &&
          this.overlayFilterTimeMs >= g.inMs &&
          this.overlayFilterTimeMs < g.outMs,
      );
      for (const graphic of activeGraphics) {
        const nx = graphic.x ?? 0.85;
        const ny = graphic.y ?? 0.1;
        const px = nx * w;
        const py = ny * h;
        const scale = graphic.scale ?? 1;
        const rotationDeg = graphic.rotationDeg ?? 0;
        const rad = (rotationDeg * Math.PI) / 180;
        const baseSize = Math.min(w, h) * 0.2 * scale;

        if (graphic.type === "emoji") {
          const emojiChar = graphic.emojiChar ?? "\u{1F600}";
          const fontSize = Math.round(72 * scale * (Math.min(w, h) / SIZE_REFERENCE_DIMENSION));
          const halfSize = fontSize * 0.6;
          this.activeLogoBounds.push({
            id: graphic.id,
            px,
            py,
            halfW: halfSize,
            halfH: halfSize,
            rad,
          });
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(rad);
          ctx.font = `${fontSize}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "#ffffff";
          ctx.fillText(emojiChar, 0, 0);
          if (this.exportIncludeEditChrome && graphic.id === this.selectedGraphicId) {
            ctx.strokeStyle = "#00aaff";
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(-halfSize - 2, -halfSize - 2, halfSize * 2 + 4, halfSize * 2 + 4);
            ctx.setLineDash([]);
          }
          ctx.restore();
          continue;
        }

        const url =
          graphic.type === "logo"
            ? this.getLogoImageUrl(graphic.logoKey)
            : getFruitIconUrl(graphic.iconKey);
        if (!url) continue;
        let img = this.logoImageCache.get(url);
        if (!img) {
          img = new Image();
          img.crossOrigin = "anonymous";
          img.src = url;
          this.logoImageCache.set(url, img);
        }
        if (!img.complete || !img.naturalWidth) continue;
        const aspect = img.naturalWidth / img.naturalHeight;
        const drawW = aspect >= 1 ? baseSize : baseSize * aspect;
        const drawH = aspect >= 1 ? baseSize / aspect : baseSize;
        this.activeLogoBounds.push({
          id: graphic.id,
          px,
          py,
          halfW: drawW / 2,
          halfH: drawH / 2,
          rad,
        });
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(rad);
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        if (this.exportIncludeEditChrome && graphic.id === this.selectedGraphicId) {
          ctx.strokeStyle = "#00aaff";
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 4]);
          ctx.strokeRect(-drawW / 2 - 2, -drawH / 2 - 2, drawW + 4, drawH + 4);
          ctx.setLineDash([]);
        }
        ctx.restore();
      }
    }

    if (this.gfxManager) {
      this.drawNonSqzGfx(ctx, w, h, visible);
    } else {
      this.activeGfxBounds = [];
    }
  }

  /** Draw non-sqz GFX snapshots using the existing Step 1 path. */
  private drawNonSqzGfx(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    visible: { timelineGfxId: string; localMs: number; graphic: ClippingStudioGraphicEvent }[],
  ): void {
    const manager = this.gfxManager!;
    this.activeGfxBounds = [];
    for (const entry of visible) {
      const snapshot = manager.captureSnapshot(entry.timelineGfxId);
      if (!snapshot) continue;
      const g = entry.graphic;
      const base = manager.getCurrentBaseStageSize();
      const gfxDrawW = snapshot.width * (w / base.w);
      const gfxDrawH = snapshot.height * (h / base.h);
      const gfxX = (g.x ?? 0) * w;
      const gfxY = (g.y ?? 0) * h;
      ctx.drawImage(snapshot, gfxX, gfxY, gfxDrawW, gfxDrawH);
      if (this.isEditMode && g.isSqz !== true) {
        this.activeGfxBounds.push({
          id: g.id,
          left: gfxX,
          top: gfxY,
          right: gfxX + gfxDrawW,
          bottom: gfxY + gfxDrawH,
        });
        this.strokeNonSqzGfxEditBounds(ctx, gfxX, gfxY, gfxDrawW, gfxDrawH, g.id);
      }
    }
  }

  /** Dashed selection rect for non-SQZ GFX — same rule as emoji/logo: only when this graphic is selected. */
  private strokeNonSqzGfxEditBounds(
    ctx: CanvasRenderingContext2D,
    gfxX: number,
    gfxY: number,
    gfxDrawW: number,
    gfxDrawH: number,
    graphicId: string,
  ): void {
    if (!this.exportIncludeEditChrome || !this.isEditMode || graphicId !== this.selectedGraphicId) {
      return;
    }
    const pad = 2;
    ctx.save();
    ctx.strokeStyle = "#00aaff";
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(gfxX - pad, gfxY - pad, gfxDrawW + pad * 2, gfxDrawH + pad * 2);
    ctx.setLineDash([]);
    ctx.restore();
  }

  /**
   * Redraws squeezable overlay content (text + logos/icons/emojis) within an already-applied
   * squeeze transform. Called from drawSqzFrame to keep overlays aligned with squeezed video.
   */
  private drawSqueezableOverlayContent(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
  ): void {
    if (!this.isEditMode) {
      this.activeTextBounds = [];
      this.activeLogoBounds = [];
      return;
    }

    this.drawTextOverlays(ctx, w, h);

    if (this.graphics?.length) {
      const activeGraphics = this.graphics.filter(
        (g) =>
          (g.type === "logo" || g.type === "fruit-icon" || g.type === "emoji") &&
          this.overlayFilterTimeMs >= g.inMs &&
          this.overlayFilterTimeMs < g.outMs,
      );
      this.activeLogoBounds = [];
      for (const graphic of activeGraphics) {
        const nx = graphic.x ?? 0.85;
        const ny = graphic.y ?? 0.1;
        const px = nx * w;
        const py = ny * h;
        const scale = graphic.scale ?? 1;
        const rotationDeg = graphic.rotationDeg ?? 0;
        const rad = (rotationDeg * Math.PI) / 180;
        const baseSize = Math.min(w, h) * 0.2 * scale;

        if (graphic.type === "emoji") {
          const emojiChar = graphic.emojiChar ?? "\u{1F600}";
          const fontSize = Math.round(72 * scale * (Math.min(w, h) / SIZE_REFERENCE_DIMENSION));
          const halfSize = fontSize * 0.6;
          this.activeLogoBounds.push({
            id: graphic.id,
            px,
            py,
            halfW: halfSize,
            halfH: halfSize,
            rad,
          });
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(rad);
          ctx.font = `${fontSize}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "#ffffff";
          ctx.fillText(emojiChar, 0, 0);
          if (this.exportIncludeEditChrome && graphic.id === this.selectedGraphicId) {
            ctx.strokeStyle = "#00aaff";
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(-halfSize - 2, -halfSize - 2, halfSize * 2 + 4, halfSize * 2 + 4);
            ctx.setLineDash([]);
          }
          ctx.restore();
          continue;
        }

        const url =
          graphic.type === "logo"
            ? this.getLogoImageUrl(graphic.logoKey)
            : getFruitIconUrl(graphic.iconKey);
        if (!url) continue;
        let img = this.logoImageCache.get(url);
        if (!img) {
          img = new Image();
          img.crossOrigin = "anonymous";
          img.src = url;
          this.logoImageCache.set(url, img);
        }
        if (!img.complete || !img.naturalWidth) continue;
        const aspect = img.naturalWidth / img.naturalHeight;
        const drawW = aspect >= 1 ? baseSize : baseSize * aspect;
        const drawH = aspect >= 1 ? baseSize / aspect : baseSize;
        this.activeLogoBounds.push({
          id: graphic.id,
          px,
          py,
          halfW: drawW / 2,
          halfH: drawH / 2,
          rad,
        });
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(rad);
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        if (this.exportIncludeEditChrome && graphic.id === this.selectedGraphicId) {
          ctx.strokeStyle = "#00aaff";
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 4]);
          ctx.strokeRect(-drawW / 2 - 2, -drawH / 2 - 2, drawW + 4, drawH + 4);
          ctx.setLineDash([]);
        }
        ctx.restore();
      }
    } else {
      this.activeLogoBounds = [];
    }
  }

  /**
   * Draws the text overlays visible at the current overlay time and refreshes their hit-test
   * bounds. Geometry comes from the shared layout helper so the preview matches the render.
   */
  private drawTextOverlays(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
  ): void {
    this.activeTextBounds = [];
    if (!this.text?.length) return;

    const activeText = this.text.filter(
      (t) => this.overlayFilterTimeMs >= t.inMs && this.overlayFilterTimeMs < t.outMs,
    );
    const measureText = (fontSpec: string, value: string): number => {
      ctx.font = fontSpec;
      return ctx.measureText(value).width;
    };

    for (const item of activeText) {
      const layout = computeTextLayout({
        item,
        canvasW: w,
        canvasH: h,
        cssFamily: this.fontService.getCssFamily(item.fontFamily),
        measureText,
      });
      this.activeTextBounds.push({ id: item.id, ...layout.bounds });
      this.drawTextItem(ctx, item, layout);
    }
  }

  /** Plate first, then glyphs with the shadow, so the shadow never darkens the plate. */
  private drawTextItem(
    ctx: CanvasRenderingContext2D,
    item: ClippingStudioTextEvent,
    layout: TextLayout,
  ): void {
    ctx.save();
    ctx.font = layout.fontSpec;
    ctx.textAlign = layout.textAlign;
    ctx.textBaseline = "top";

    if (layout.plate) {
      const { color, left, top, width, height, radius } = layout.plate;
      ctx.fillStyle = color;
      if (radius > 0) {
        this.fillRoundedRect(ctx, left, top, width, height, radius);
      } else {
        ctx.fillRect(left, top, width, height);
      }
    }

    if (layout.shadow) {
      ctx.shadowColor = layout.shadow.color;
      ctx.shadowBlur = layout.shadow.blur;
      ctx.shadowOffsetX = layout.shadow.offsetX;
      ctx.shadowOffsetY = layout.shadow.offsetY;
    }
    ctx.fillStyle = item.fontColor ?? "#ffffff";
    ctx.fillText(item.text, layout.anchorX, layout.glyphTop);
    ctx.restore();

    if (this.exportIncludeEditChrome && item.id === this.selectedTextId) {
      const { left, top, right, bottom } = layout.bounds;
      ctx.save();
      ctx.strokeStyle = "#00aaff";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(left - 2, top - 2, right - left + 4, bottom - top + 4);
      ctx.restore();
    }
  }

  private fillRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") {
      ctx.roundRect(x, y, w, h, r);
    } else {
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.arc(x + w - r, y + r, r, -Math.PI / 2, 0);
      ctx.lineTo(x + w, y + h - r);
      ctx.arc(x + w - r, y + h - r, r, 0, Math.PI / 2);
      ctx.lineTo(x + r, y + h);
      ctx.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
      ctx.lineTo(x, y + r);
      ctx.arc(x + r, y + r, r, Math.PI, (3 * Math.PI) / 2);
      ctx.closePath();
    }
    ctx.fill();
  }

  /**
   * Loads font files used by active text overlays / subtitle style so canvas
   * redraws pick up registered FontFace names via getCssFamily.
   */
  private ensurePreviewFontsLoaded(): void {
    const names = new Set<string>();
    const subtitleFamily = this.subtitleStyle?.fontFamily;
    if (subtitleFamily) {
      names.add(subtitleFamily);
    }
    for (const item of this.text ?? []) {
      if (item.fontFamily) {
        names.add(item.fontFamily);
      }
    }
    if (names.size === 0) {
      return;
    }

    const token = ++this.fontEnsureToken;
    void Promise.all(
      [...names].map((name) => this.fontService.ensureFontLoaded(name)),
    ).then(() => {
      if (token !== this.fontEnsureToken) {
        return;
      }
      this.drawSubtitles();
    });
  }

  /**
   * Draws subtitles onto the dedicated subtitles canvas (always-on-top, never squeezable).
   * Called from ngOnChanges when subtitle-related inputs change.
   */
  private drawSubtitles(): void {
    const canvas = this.subtitlesCanvasRef?.nativeElement;
    if (!canvas?.width || !canvas?.height) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const w = canvas.width;
    const h = canvas.height;

    if (this.captionsOn && this.subtitles?.length) {
      const style = this.subtitleStyle ?? DEFAULT_SUBTITLE_STYLE;
      const active = this.subtitles.filter(
        (s) => this.currentTimeMs >= s.StartTimeMS && this.currentTimeMs < s.EndTimeMS,
      );
      if (!active.length) return;
      const padding = SUBTITLE_BOX_PADDING;
      ctx.font = `${style.fontSize}px ${this.fontService.getCssFamily(style.fontFamily)}`;
      ctx.textAlign = "center";
      ctx.fillStyle = style.fontColor;
      ctx.strokeStyle = style.backgroundColor;

      for (let i = 0; i < active.length; i++) {
        const segment = active[i];
        const lines = segment.Text.split(/\n/).filter((t) => t.trim().length > 0);
        if (lines.length === 0) continue;

        const lineHeight = style.fontSize * SUBTITLE_LINE_HEIGHT_RATIO;
        const blockHeight = lines.length * lineHeight;
        let y: number;
        if (style.position === "top") {
          y = padding + style.fontSize;
        } else if (style.position === "middle") {
          y = h / 2 - blockHeight / 2 + style.fontSize;
        } else {
          y = h - padding - blockHeight + style.fontSize;
        }

        const maxLineW = Math.max(
          ...lines.map((line) => ctx.measureText(line).width),
        );
        const boxW = maxLineW + padding * 2;
        const boxH = blockHeight + padding;
        const boxX = (w - boxW) / 2;
        const boxY = y - style.fontSize - padding * 0.5;

        const availableWidth = w - padding * 2;
        const minScale = 0.25;
        const scale = maxLineW > 0 && availableWidth > 0
          ? Math.min(1, Math.max(minScale, availableWidth / maxLineW))
          : 1;

        if (scale < 1) {
          const cx = w / 2;
          const cy = boxY + boxH / 2;
          ctx.save();
          ctx.translate(cx, cy);
          ctx.scale(scale, scale);
          ctx.translate(-cx, -cy);
        }

        if (style.backgroundStyle === "solid") {
          ctx.fillStyle = style.backgroundColor;
          ctx.fillRect(boxX, boxY, boxW, boxH);
        }
        if (style.backgroundStyle === "dropShadow") {
          ctx.shadowColor = SUBTITLE_SHADOW_COLOR;
          ctx.shadowBlur = SUBTITLE_SHADOW_BLUR;
          ctx.shadowOffsetX = SUBTITLE_SHADOW_OFFSET_X;
          ctx.shadowOffsetY = SUBTITLE_SHADOW_OFFSET_Y;
        }
        ctx.fillStyle = style.fontColor;

        for (let L = 0; L < lines.length; L++) {
          const lineY = y + L * lineHeight;
          if (style.backgroundStyle === "outline") {
            ctx.strokeStyle = style.backgroundColor;
            ctx.lineWidth = Math.max(2, Math.floor(style.fontSize / 12));
            ctx.strokeText(lines[L], w / 2, lineY);
            ctx.fillText(lines[L], w / 2, lineY);
          } else {
            ctx.fillText(lines[L], w / 2, lineY);
          }
        }
        if (style.backgroundStyle === "dropShadow") {
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        }

        if (scale < 1) {
          ctx.restore();
        }
      }
    }
  }

  private getLogoImageUrl(logoKey: string | undefined): string {
    if (!logoKey) return "";
    return LOGO_URL_MAP[logoKey] ?? "";
  }

  private getCanvasCoords(event: PointerEvent): { x: number; y: number } | null {
    const overlay = this.mainOverlayCanvasRef?.nativeElement;
    if (!overlay?.width || !overlay?.height) return null;
    const rect = overlay.getBoundingClientRect();
    const scaleX = overlay.width / rect.width;
    const scaleY = overlay.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  /**
   * SQZ draws squeezable overlay content under `ctx.translate(leftPx,topPx); ctx.scale(scaleX,scaleY)`.
   * Hit-test bounds and drag math are computed in **pre-transform** canvas space; map pointer coords
   * from post-transform (what the user sees) back to pre-transform for interaction parity.
   */
  private mapOverlayPointerToPreSqzIfNeeded(coords: { x: number; y: number }): { x: number; y: number } {
    const p: any = this.lastRenderPayload;
    if (!p || p.mode !== "sqz") return coords;

    const leftPx = Number(p.leftPx);
    const topPx = Number(p.topPx);
    const scaleX = Number(p.scaleX);
    const scaleY = Number(p.scaleY);
    if (!Number.isFinite(leftPx) || !Number.isFinite(topPx) || !Number.isFinite(scaleX) || !Number.isFinite(scaleY)) {
      return coords;
    }
    if (Math.abs(scaleX) < 1e-6 || Math.abs(scaleY) < 1e-6) {
      return coords;
    }

    return {
      x: (coords.x - leftPx) / scaleX,
      y: (coords.y - topPx) / scaleY,
    };
  }

  private hitTestText(canvasX: number, canvasY: number): string | null {
    for (let i = this.activeTextBounds.length - 1; i >= 0; i--) {
      const b = this.activeTextBounds[i];
      if (canvasX >= b.left && canvasX <= b.right && canvasY >= b.top && canvasY <= b.bottom) {
        return b.id;
      }
    }
    return null;
  }

  /** Point (canvasX, canvasY) inside any currently drawn non-SQZ GFX snapshot rect? Topmost wins. */
  private hitTestGfx(canvasX: number, canvasY: number): string | null {
    for (let i = this.activeGfxBounds.length - 1; i >= 0; i--) {
      const b = this.activeGfxBounds[i];
      if (canvasX >= b.left && canvasX <= b.right && canvasY >= b.top && canvasY <= b.bottom) {
        return b.id;
      }
    }
    return null;
  }

  /** Point (canvasX, canvasY) in logo/fruit-icon rotated rect? Transform to local space and check. */
  private hitTestLogo(canvasX: number, canvasY: number): string | null {
    for (let i = this.activeLogoBounds.length - 1; i >= 0; i--) {
      const b = this.activeLogoBounds[i];
      const dx = canvasX - b.px;
      const dy = canvasY - b.py;
      const cos = Math.cos(-b.rad);
      const sin = Math.sin(-b.rad);
      const localX = dx * cos - dy * sin;
      const localY = dx * sin + dy * cos;
      if (Math.abs(localX) <= b.halfW && Math.abs(localY) <= b.halfH) {
        return b.id;
      }
    }
    return null;
  }

  onOverlayPointerDown(event: PointerEvent): void {
    if (!this.isEditMode) return;
    const coords = this.getCanvasCoords(event);
    if (!coords) return;
    const hitCoords = this.mapOverlayPointerToPreSqzIfNeeded(coords);
    const overlay = this.mainOverlayCanvasRef?.nativeElement;
    if (!overlay?.width || !overlay?.height) return;
    const gfxId = this.hitTestGfx(hitCoords.x, hitCoords.y);
    const logoId = gfxId ? null : this.hitTestLogo(hitCoords.x, hitCoords.y);
    const textId = gfxId || logoId ? null : this.hitTestText(hitCoords.x, hitCoords.y);
    this.hitGraphicId = gfxId ?? logoId ?? null;
    this.hitTextId = this.hitGraphicId ? null : textId;
    this.pointerDownCanvasX = hitCoords.x;
    this.pointerDownCanvasY = hitCoords.y;
    if (gfxId || logoId || textId) {
      this.capturedPointerId = event.pointerId;
      overlay.setPointerCapture(event.pointerId);
    }
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = null;
      if (this.hitGraphicId && this.graphics) {
        const graphic = this.graphics.find((g) => g.id === this.hitGraphicId);
        if (graphic) {
          this.isDraggingGraphic = true;
          if (graphic.type === "gfx") {
            this.dragInitialNormX = graphic.x ?? 0;
            this.dragInitialNormY = graphic.y ?? 0;
          } else {
            this.dragInitialNormX = graphic.x ?? 0.85;
            this.dragInitialNormY = graphic.y ?? 0.1;
          }
          this.dragInitialCanvasX = this.pointerDownCanvasX;
          this.dragInitialCanvasY = this.pointerDownCanvasY;
          this.graphicDragStart.emit();
        }
      } else if (this.hitTextId && this.text) {
        const item = this.text.find((t) => t.id === this.hitTextId);
        if (item) {
          this.isDragging = true;
          this.dragInitialNormX = item.x ?? 0.1;
          this.dragInitialNormY = item.y ?? 0.1;
          this.dragInitialCanvasX = this.pointerDownCanvasX;
          this.dragInitialCanvasY = this.pointerDownCanvasY;
          this.textDragStart.emit();
        }
      }
    }, ClippingStudioVideoPreviewComponent.LONG_PRESS_MS);
  }

  onOverlayPointerMove(event: PointerEvent): void {
    const coords = this.getCanvasCoords(event);
    if (!coords || !this.mainOverlayCanvasRef?.nativeElement) return;
    const pointer = this.mapOverlayPointerToPreSqzIfNeeded(coords);
    const w = this.mainOverlayCanvasRef.nativeElement.width;
    const h = this.mainOverlayCanvasRef.nativeElement.height;
    if (this.isDraggingGraphic && this.hitGraphicId) {
      const dx = pointer.x - this.dragInitialCanvasX;
      const dy = pointer.y - this.dragInitialCanvasY;
      let newX = this.dragInitialNormX + dx / w;
      let newY = this.dragInitialNormY + dy / h;
      newX = Math.max(0, Math.min(1, newX));
      newY = Math.max(0, Math.min(1, newY));
      this.graphicPositionChange.emit({
        id: this.hitGraphicId,
        x: newX,
        y: newY,
        isDragging: true,
      });
    } else if (this.isDragging && this.hitTextId) {
      const dx = pointer.x - this.dragInitialCanvasX;
      const dy = pointer.y - this.dragInitialCanvasY;
      let newX = this.dragInitialNormX + dx / w;
      let newY = this.dragInitialNormY + dy / h;
      newX = Math.max(0, Math.min(1, newX));
      newY = Math.max(0, Math.min(1, newY));
      this.textPositionChange.emit({ id: this.hitTextId, x: newX, y: newY, isDragging: true });
    }
  }

  onOverlayPointerUp(event: PointerEvent): void {
    if (this.capturedPointerId != null) {
      const overlay = this.mainOverlayCanvasRef?.nativeElement;
      try {
        overlay?.releasePointerCapture(this.capturedPointerId);
      } catch {
        // ignore if already released
      }
      this.capturedPointerId = null;
    }
    if (this.longPressTimer != null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    if (this.isDraggingGraphic) {
      this.isDraggingGraphic = false;
      this.graphicDragEnd.emit();
      this.hitGraphicId = null;
      return;
    }
    if (this.isDragging) {
      this.isDragging = false;
      this.textDragEnd.emit();
      this.hitTextId = null;
      return;
    }
    const coords = this.getCanvasCoords(event);
    const up = coords ? this.mapOverlayPointerToPreSqzIfNeeded(coords) : null;
    const moved =
      up &&
      (Math.abs(up.x - this.pointerDownCanvasX) > ClippingStudioVideoPreviewComponent.MOVE_THRESHOLD_PX ||
        Math.abs(up.y - this.pointerDownCanvasY) > ClippingStudioVideoPreviewComponent.MOVE_THRESHOLD_PX);
    if (this.hitGraphicId != null && !moved) {
      this.graphicSelected.emit(this.hitGraphicId);
    }
    this.hitGraphicId = null;
    if (this.hitTextId != null && !moved) {
      this.textSelected.emit(this.hitTextId);
    }
    this.hitTextId = null;
  }

  onOverlayPointerLeave(): void {
    if (this.capturedPointerId != null) {
      const overlay = this.mainOverlayCanvasRef?.nativeElement;
      try {
        overlay?.releasePointerCapture(this.capturedPointerId);
      } catch {
        // ignore if already released
      }
      this.capturedPointerId = null;
    }
    if (this.longPressTimer != null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    if (this.isDraggingGraphic) {
      this.isDraggingGraphic = false;
      this.graphicDragEnd.emit();
    }
    if (this.isDragging) {
      this.isDragging = false;
      this.textDragEnd.emit();
    }
    this.hitGraphicId = null;
    this.hitTextId = null;
  }

  /**
   * WYSIWYG clip-preview export: draws base (reframe or letterbox fit + SQZ) and overlays
   * (GFX/text/emoji/logo) onto an offscreen canvas. No subtitles, no edit chrome.
   * Caller supplies an already-seeked HTMLVideoElement (typically ephemeral TranscodedFile).
   */
  async captureComposedClipPreviewFromVideo(args: {
    video: HTMLVideoElement;
    aspectRatio: ClipPreviewAspectRatio;
    reframeConfig?: ReframeConfig | null;
    maxHeight?: number;
    jpegQuality?: number;
    /** Override overlay in/out filtering time (edit timeline ms). */
    overlayTimeMs?: number | null;
  }): Promise<string | null> {
    const video = args.video;
    if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      return null;
    }

    const maxHeight = args.maxHeight ?? CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT;
    const jpegQuality = args.jpegQuality ?? CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY;
    const { width: outW, height: outH } = dimensionsForAspectMaxHeight(
      maxHeight,
      args.aspectRatio,
    );

    const savedOverlayOverride = this.overlayTimeMsOverride;
    this.exportIncludeEditChrome = false;
    if (args.overlayTimeMs !== undefined) {
      this.overlayTimeMsOverride = args.overlayTimeMs;
    }

    const cfg = args.reframeConfig !== undefined ? args.reframeConfig : this.reframeConfig;

    try {
      await this.ensureActiveOverlayImagesLoaded();

      const decision = this.decideAndPrepareFrame(outW, outH);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, outW, outH);

      if (cfg) {
        const configToDraw =
          cfg.layout === "fill" && !cfg.crop
            ? { ...cfg, crop: this.getFullFillCropForVideo(video, outW, outH) }
            : cfg;
        drawReframeFrame(video, ctx, outW, outH, configToDraw, decision.dstRect2d);
      } else {
        this.drawVideoContain(video, ctx, decision.dstRect2d ?? { x: 0, y: 0, w: outW, h: outH });
      }

      this.drawOverlayContentOnto(ctx, outW, outH, decision.payload);
      return canvas.toDataURL("image/jpeg", jpegQuality);
    } catch {
      return null;
    } finally {
      this.exportIncludeEditChrome = true;
      this.overlayTimeMsOverride = savedOverlayOverride;
    }
  }

  private getFullFillCropForVideo(
    video: HTMLVideoElement,
    outW: number,
    outH: number,
  ): { x: number; y: number; width: number; height: number } {
    const vidW = video.videoWidth;
    const vidH = video.videoHeight;
    if (!vidW || !vidH || !outW || !outH) {
      return { x: 0, y: 0, width: 1, height: 1 };
    }
    const targetAspect = outW / outH;
    const sourceAspect = vidW / vidH;
    if (sourceAspect > targetAspect) {
      const width = targetAspect / sourceAspect;
      return { x: (1 - width) / 2, y: 0, width, height: 1 };
    }
    const height = sourceAspect / targetAspect;
    return { x: 0, y: (1 - height) / 2, width: 1, height };
  }

  private drawVideoContain(
    video: HTMLVideoElement,
    ctx: CanvasRenderingContext2D,
    dst: { x: number; y: number; w: number; h: number },
  ): void {
    const vidW = video.videoWidth;
    const vidH = video.videoHeight;
    if (!vidW || !vidH || dst.w <= 0 || dst.h <= 0) return;
    const scale = Math.min(dst.w / vidW, dst.h / vidH);
    const drawW = vidW * scale;
    const drawH = vidH * scale;
    const dx = dst.x + (dst.w - drawW) / 2;
    const dy = dst.y + (dst.h - drawH) / 2;
    ctx.drawImage(video, 0, 0, vidW, vidH, dx, dy, drawW, drawH);
  }

  private async ensureActiveOverlayImagesLoaded(): Promise<void> {
    if (!this.isEditMode || !this.graphics?.length) return;
    const t = this.overlayFilterTimeMs;
    const urls: string[] = [];
    for (const graphic of this.graphics) {
      if (t < graphic.inMs || t >= graphic.outMs) continue;
      if (graphic.type === "logo") {
        const url = this.getLogoImageUrl(graphic.logoKey);
        if (url) urls.push(url);
      } else if (graphic.type === "fruit-icon") {
        const url = getFruitIconUrl(graphic.iconKey);
        if (url) urls.push(url);
      }
    }
    await Promise.all(
      urls.map(
        (url) =>
          new Promise<void>((resolve) => {
            let img = this.logoImageCache.get(url);
            if (!img) {
              img = new Image();
              img.crossOrigin = "anonymous";
              img.src = url;
              this.logoImageCache.set(url, img);
            }
            if (img.complete && img.naturalWidth) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve();
          }),
      ),
    );
  }

  /** Draws overlay content onto an arbitrary 2D context (used by live overlay canvas and export). */
  private drawOverlayContentOnto(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    payload: unknown,
  ): void {
    // Defense-in-depth: never composite GFX/SQZ outside EDIT (incl. stale payloads).
    if (!this.isEditMode) {
      this.drawNonSqzOverlay(ctx, w, h, []);
      return;
    }

    const p: any = payload ?? this.lastRenderPayload;
    if (!p || p.mode !== "sqz") {
      this.drawNonSqzOverlay(ctx, w, h, p?.visible ?? []);
      return;
    }

    const manager = this.gfxManager;
    if (!manager) return;

    ctx.save();
    ctx.translate(p.leftPx, p.topPx);
    ctx.scale(p.scaleX, p.scaleY);
    this.drawSqueezableOverlayContent(ctx, w, h);
    this.activeGfxBounds = [];
    for (const entry of p.visible ?? []) {
      if (entry.graphic?.isSqz === true) continue;
      const snapshot = manager.captureSnapshot(entry.timelineGfxId);
      if (!snapshot) continue;
      const g = entry.graphic;
      const base = manager.getCurrentBaseStageSize();
      const gfxDrawW = snapshot.width * (w / base.w);
      const gfxDrawH = snapshot.height * (h / base.h);
      const gfxX = (g.x ?? 0) * w;
      const gfxY = (g.y ?? 0) * h;
      ctx.drawImage(snapshot, gfxX, gfxY, gfxDrawW, gfxDrawH);
      if (this.isEditMode) {
        this.activeGfxBounds.push({
          id: g.id,
          left: gfxX,
          top: gfxY,
          right: gfxX + gfxDrawW,
          bottom: gfxY + gfxDrawH,
        });
        this.strokeNonSqzGfxEditBounds(ctx, gfxX, gfxY, gfxDrawW, gfxDrawH, g.id);
      }
    }
    ctx.restore();

    ctx.drawImage(p.sqzSnapshot, 0, 0, w, h);
  }

  /**
   * Waits for the video to have frame data (readyState >= HAVE_CURRENT_DATA).
   * Resolves immediately if already ready, or on the first 'loadeddata' event.
   * Rejects after timeoutMs.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/loadeddata_event
   */
  private waitForVideoReady(video: HTMLVideoElement, timeoutMs: number): Promise<void> {
    if (video.readyState >= 2) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error("Video load timeout"));
      }, timeoutMs);
      const onLoadedData = (): void => {
        cleanup();
        resolve();
      };
      const cleanup = (): void => {
        clearTimeout(timer);
        video.removeEventListener("loadeddata", onLoadedData);
      };
      video.addEventListener("loadeddata", onLoadedData, { once: true });
    });
  }

  /**
   * Captures the video frame at the given time (ms) as a JPEG data URL.
   * Resolves timeline time to the correct source (main or appended). For main source, useHiddenVideo
   * uses a hidden clone so the visible video does not seek. For appended source, the appended video
   * element is sought and then restored (may briefly affect playback if that source is playing).
   * For non-main sources, waits for the video to have frame data (loadeddata / readyState >= 2) before capturing.
   * Returns null if video not ready or on timeout/failure.
   */
  async captureSnapshotAtTime(
    timeMs: number,
    options?: { useHiddenVideo?: boolean },
  ): Promise<string | null> {
    const resolved = this.resolveTimelineTimeToSource(timeMs);
    if (!resolved) return null;
    const { video: resolvedVideo, timeSec } = resolved;

    const mainVideo = this.videoRef?.nativeElement;
    const isMain = resolvedVideo === mainVideo;

    if (isMain) {
      const useHidden = options?.useHiddenVideo === true;
      const video = useHidden
        ? this.videoThumbnailCaptureRef?.nativeElement
        : mainVideo;
      if (!video || video.readyState < 2 || !video.videoWidth) return null;
      const savedTime = video.currentTime;
      const timeoutMs = 3000;
      const seekToSec = Math.max(0, timeSec);
      try {
        video.currentTime = seekToSec;
        await new Promise<void>((resolve, reject) => {
          const onSeeked = (): void => {
            cleanup();
            resolve();
          };
          const timer = setTimeout(() => {
            cleanup();
            reject(new Error("Seek timeout"));
          }, timeoutMs);
          const cleanup = (): void => {
            clearTimeout(timer);
            video.removeEventListener("seeked", onSeeked);
          };
          video.addEventListener("seeked", onSeeked, { once: true });
        });
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        if (!useHidden) {
          video.currentTime = savedTime;
          await new Promise<void>((resolve) => {
            const onSeeked = (): void => {
              clearTimeout(timer);
              video.removeEventListener("seeked", onSeeked);
              resolve();
            };
            const timer = setTimeout(onSeeked, timeoutMs);
            video.addEventListener("seeked", onSeeked, { once: true });
          });
        }
        return dataUrl;
      } catch {
        if (!useHidden) video.currentTime = savedTime;
        return null;
      }
    }

    const video = resolvedVideo;
    try {
      await this.waitForVideoReady(video, 15000);
    } catch {
      return null;
    }
    if (!video.videoWidth) return null;
    const savedTime = video.currentTime;
    const timeoutMs = 3000;
    try {
      video.currentTime = Math.max(0, timeSec);
      await new Promise<void>((resolve, reject) => {
        const onSeeked = (): void => {
          cleanup();
          resolve();
        };
        const timer = setTimeout(() => {
          cleanup();
          reject(new Error("Seek timeout"));
        }, timeoutMs);
        const cleanup = (): void => {
          clearTimeout(timer);
          video.removeEventListener("seeked", onSeeked);
        };
        video.addEventListener("seeked", onSeeked, { once: true });
      });
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      video.currentTime = savedTime;
      await new Promise<void>((resolve) => {
        const onSeeked = (): void => {
          clearTimeout(timer);
          video.removeEventListener("seeked", onSeeked);
          resolve();
        };
        const timer = setTimeout(onSeeked, timeoutMs);
        video.addEventListener("seeked", onSeeked, { once: true });
      });
      return dataUrl;
    } catch {
      video.currentTime = savedTime;
      return null;
    }
  }
}
